/*
 * Community Showcase interactions — scoped per [data-slider][data-slider-type="community-showcase"] root.
 * Hooks: [data-slider], [data-slider-track], [data-slider-prev/next], [data-media-card].
 */

// Must stay in sync with Tailwind's lg: and md: breakpoints
const rootStyle = getComputedStyle(document.documentElement);
const DESKTOP_BREAKPOINT = parseInt(rootStyle.getPropertyValue('--breakpoint-lg'), 10) || 1024;
const TABLET_BREAKPOINT = parseInt(rootStyle.getPropertyValue('--breakpoint-md'), 10) || 768;
const AUTOPLAY_INTERVAL = 3000;
const instances = new WeakMap();

// CSS ease-in-out replicated for customScrollTo
const easeInOutQuad = (t, b, c, d) => {
  t /= d / 2;
  if (t < 1) return (c / 2) * t * t + b;
  t--;
  return (-c / 2) * (t * (t - 2) - 1) + b;
};

class CommunityShowcase {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-slider-track]');
    this.controls = root.querySelector('[data-slider-controls]');
    this.prevBtn = root.querySelector('[data-slider-prev]');
    this.nextBtn = root.querySelector('[data-slider-next]');
    this.slideCount = parseInt(root.dataset.slideCount, 10) || 0;
    this.desktopVisible = parseInt(root.dataset.sliderDesktopVisible, 10) || 5;

    this.currentIndex = 0;
    this.itemsVisible = 1;
    this.initialOffset = 0;
    this.cardWidth = 0;
    this.windowWidth = window.innerWidth;

    this.clonesAdded = false;
    this.cloneCount = 0;
    this.cards = null;

    this.autoplayTimer = null;
    this.snapTimer = null;
    this.scrollSettleTimer = null;
    this.scrollFrame = null;
    this.resizeFrame = null;
    this.scrollAnimationId = null;
    this.pendingSnap = null;

    this.isPointerPaused = false;
    this.isFocusPaused = false;

    this.abortController = new AbortController();
    this.reducedMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');

    if (!this.track || this.slideCount === 0) return;
    this.init();
  }

  init() {
    this.updateVisibility();
    this.updateControlsVisibility();
    this.syncClones();
    this.measureDimensions();
    this.applyInstantPosition();
    this.bindEvents();
    this.updateAutoplay();
  }

  getCards() {
    if (!this.cards) this.cards = this.track.querySelectorAll('[data-media-card]');
    return this.cards;
  }

  measureDimensions() {
    const first = this.getCards()[0];
    if (!first) return;
    this.initialOffset = first.offsetLeft;
    this.cardWidth = first.offsetWidth || this.track.offsetWidth;
  }

  clearTappedState() {
    this.track.querySelectorAll('[data-media-card][data-tapped]').forEach((c) => delete c.dataset.tapped);
  }

  cloneCard(sourceCard, beforeNode) {
    const clone = sourceCard.cloneNode(true);
    clone.dataset.clone = 'true';
    clone.setAttribute('aria-hidden', 'true'); clone.toggleAttribute('inert', true); // one line: file sits at the 500-line ceiling
    clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
    if (beforeNode) this.track.insertBefore(clone, beforeNode);
    else this.track.appendChild(clone);
  }

  syncClones() {
    // Threshold so clones never appear in the visible area on the widest breakpoint
    const shouldHaveClones = this.slideCount > this.desktopVisible;
    if (shouldHaveClones && !this.clonesAdded) this.setupClones();
    else if (!shouldHaveClones && this.clonesAdded) this.teardownClones();
  }

  setupClones() {
    const realCards = this.track.querySelectorAll('[data-media-card]:not([data-clone])');
    if (realCards.length === 0) return;
    const N = Math.min(this.desktopVisible, realCards.length);
    this.cloneCount = N;

    for (let i = 0; i < N; i++) this.cloneCard(realCards[i]);

    // Repeating insertBefore against the same reference accumulates nodes immediately
    // before it, preserving source order: [last-N..., realCards[0], ...]
    for (let i = realCards.length - N; i < realCards.length; i++) {
      this.cloneCard(realCards[i], realCards[0]);
    }

    this.clonesAdded = true;
    this.cards = null;
  }

  teardownClones() {
    if (!this.clonesAdded) return;
    this.track.querySelectorAll('[data-media-card][data-clone]').forEach((el) => el.remove());
    this.clonesAdded = false;
    this.cloneCount = 0;
    this.cards = null;
    if (this.snapTimer) {
      clearTimeout(this.snapTimer);
      this.snapTimer = null;
    }
    this.pendingSnap = null;
  }

  setTrackPosition(delta, { instant = false, duration = 300 } = {}) {
    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;

    if (isDesktop) {
      this.track.style.transitionDuration = instant ? '0ms' : `${duration}ms`;
      this.track.style.transform = `translateX(-${delta}px)`;
      if (instant) {
        // eslint-disable-next-line no-unused-expressions
        this.track.offsetHeight; // reflow so the next transition starts here
        this.track.style.transitionDuration = '';
      }
      return;
    }

    this.track.style.transitionDuration = '';
    this.track.style.transform = '';

    if (instant) {
      const savedSnap = this.track.style.scrollSnapType;
      this.track.style.scrollSnapType = 'none';
      this.track.scrollLeft = delta;
      // eslint-disable-next-line no-unused-expressions
      this.track.offsetHeight;
      this.track.style.scrollSnapType = savedSnap;
    } else {
      this.customScrollTo(this.track, delta, duration);
    }
  }

  applyInstantPosition() {
    const target = this.getCards()[this.cloneCount + this.currentIndex];
    if (!target) return;
    this.setTrackPosition(target.offsetLeft - this.initialOffset, { instant: true });
  }

  applyPendingSnap() {
    if (!this.pendingSnap) return;
    const { domIndex } = this.pendingSnap;
    this.pendingSnap = null;
    const target = this.getCards()[domIndex];
    if (!target) return;
    this.setTrackPosition(target.offsetLeft - this.initialOffset, { instant: true });
  }

  customScrollTo(element, target, duration) {
    if (this.scrollAnimationId) cancelAnimationFrame(this.scrollAnimationId);

    const start = element.scrollLeft;
    const change = target - start;
    const startTime = performance.now();
    element.style.scrollSnapType = 'none';

    const step = (now) => {
      const elapsed = now - startTime;
      if (elapsed < duration) {
        element.scrollLeft = easeInOutQuad(elapsed, start, change, duration);
        this.scrollAnimationId = requestAnimationFrame(step);
      } else {
        element.scrollLeft = target;
        element.style.scrollSnapType = '';
        this.scrollAnimationId = null;
      }
    };

    this.scrollAnimationId = requestAnimationFrame(step);
  }

  goTo(index, isAutoplay = false) {
    const children = this.getCards();
    if (!children.length) return;

    const duration = isAutoplay ? 600 : 300;
    let domIndex;
    let realIndex;
    let needsSnap = false;

    if (this.clonesAdded) {
      // Execute any pending snap so a rapid click animates from the real position,
      // not from the clone we were animating toward
      if (this.snapTimer) {
        clearTimeout(this.snapTimer);
        this.snapTimer = null;
        this.applyPendingSnap();
      }

      const N = this.cloneCount;
      const lastReal = this.slideCount - 1;

      if (index > lastReal) {
        domIndex = N + this.slideCount;
        realIndex = 0;
        needsSnap = true;
      } else if (index < 0) {
        domIndex = N - 1;
        realIndex = lastReal;
        needsSnap = true;
      } else {
        domIndex = N + index;
        realIndex = index;
      }
    } else {
      const maxIndex = Math.max(0, this.slideCount - this.itemsVisible);
      realIndex = Math.max(0, Math.min(index, maxIndex));
      domIndex = realIndex;
    }

    this.currentIndex = realIndex;
    const target = children[domIndex];
    if (!target) return;

    this.setTrackPosition(target.offsetLeft - this.initialOffset, { duration });

    if (needsSnap) {
      this.pendingSnap = { domIndex: this.cloneCount + realIndex };
      // +50ms buffer so customScrollTo's RAF has fully settled before we snap
      this.snapTimer = setTimeout(() => {
        this.snapTimer = null;
        this.applyPendingSnap();
      }, duration + 50);
    }
  }

  handleScroll() {
    if (!this.cardWidth) return;
    const scrolledDomIndex = Math.round(this.track.scrollLeft / this.cardWidth);
    const realIndex = this.domToRealIndex(scrolledDomIndex);
    const clamped = Math.max(0, Math.min(realIndex, this.slideCount - 1));
    if (clamped !== this.currentIndex) this.currentIndex = clamped;
  }

  domToRealIndex(domIndex) {
    if (!this.clonesAdded) return domIndex;
    const N = this.cloneCount;
    if (domIndex < N) return this.slideCount - N + domIndex;
    if (domIndex >= N + this.slideCount) return domIndex - N - this.slideCount;
    return domIndex - N;
  }

  maybeResetCloneRegion() {
    // Only runs for mobile native-scroll wrap; desktop loop is handled by goTo's snap timer
    if (!this.clonesAdded) return;
    if (window.innerWidth >= DESKTOP_BREAKPOINT) return;
    if (this.scrollAnimationId || this.snapTimer) return;
    if (!this.cardWidth) return;

    const scrolledDomIndex = Math.round(this.track.scrollLeft / this.cardWidth);
    const N = this.cloneCount;
    let targetDomIndex = null;
    if (scrolledDomIndex < N) targetDomIndex = scrolledDomIndex + this.slideCount;
    else if (scrolledDomIndex >= N + this.slideCount) targetDomIndex = scrolledDomIndex - this.slideCount;

    if (targetDomIndex === null) return;
    const target = this.getCards()[targetDomIndex];
    if (!target) return;
    this.setTrackPosition(target.offsetLeft - this.initialOffset, { instant: true });
  }

  updateVisibility() {
    const w = window.innerWidth;
    this.itemsVisible = w >= DESKTOP_BREAKPOINT ? this.desktopVisible : w >= TABLET_BREAKPOINT ? 4 : 2;
  }

  updateControlsVisibility() {
    if (!this.controls) return;
    const show = window.innerWidth >= DESKTOP_BREAKPOINT;
    this.controls.dataset.visible = String(show);
    this.controls.toggleAttribute('inert', !show); if (show) { this.controls.removeAttribute('aria-hidden'); } else { this.controls.setAttribute('aria-hidden', 'true'); } // one line: file sits at the 500-line ceiling
  }

  updateAutoplay() {
    if (this.shouldAutoplay()) this.startAutoplay();
    else this.stopAutoplay();
  }

  shouldAutoplay() {
    return (
      this.slideCount > this.itemsVisible &&
      document.visibilityState !== 'hidden' &&
      !this.reducedMotionQuery?.matches &&
      !this.isPointerPaused &&
      !this.isFocusPaused
    );
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      if (this.clonesAdded) {
        this.goTo(this.currentIndex + 1, true);
      } else {
        const maxIndex = Math.max(0, this.slideCount - this.itemsVisible);
        this.goTo(this.currentIndex >= maxIndex ? 0 : this.currentIndex + 1, true);
      }
    }, AUTOPLAY_INTERVAL);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  bindEvents() {
    const { signal } = this.abortController;

    this.prevBtn?.addEventListener('click', () => {
      this.goTo(this.currentIndex - 1);
      this.updateAutoplay();
    }, { signal });

    this.nextBtn?.addEventListener('click', () => {
      this.goTo(this.currentIndex + 1);
      this.updateAutoplay();
    }, { signal });

    this.track.addEventListener('mouseenter', () => {
      this.isPointerPaused = true;
      this.updateAutoplay();
    }, { signal });

    this.track.addEventListener('mouseleave', () => {
      this.isPointerPaused = false;
      this.updateAutoplay();
    }, { signal });

    // Pause only when focus is on card content (inside the track), not on our prev/next controls
    this.root.addEventListener('focusin', (event) => {
      if (!this.track.contains(event.target)) return;
      this.isFocusPaused = true;
      this.updateAutoplay();
    }, { signal });

    this.root.addEventListener('focusout', (event) => {
      if (event.relatedTarget && this.track.contains(event.relatedTarget)) return;
      this.isFocusPaused = false;
      this.updateAutoplay();
    }, { signal });

    // First tap reveals the overlay, second tap follows the link (touch only)
    this.track.addEventListener('click', (e) => {
      const card = e.target.closest('[data-media-card]');
      if (!card) return;
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      if (card.dataset.tapped === undefined) {
        e.preventDefault();
        this.clearTappedState();
        card.dataset.tapped = '';
      }
    }, { signal });

    this.track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.goTo(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
      this.updateAutoplay();
    }, { signal });

    this.track.addEventListener('touchstart', (e) => {
      // User takes over — cancel anything programmatic in flight
      if (this.scrollAnimationId) {
        cancelAnimationFrame(this.scrollAnimationId);
        this.scrollAnimationId = null;
        this.track.style.scrollSnapType = '';
      }
      if (this.snapTimer) {
        clearTimeout(this.snapTimer);
        this.snapTimer = null;
      }
      if (this.scrollSettleTimer) {
        clearTimeout(this.scrollSettleTimer);
        this.scrollSettleTimer = null;
      }
      // Pause autoplay during touch (mouseenter doesn't fire reliably on touch devices)
      this.isPointerPaused = true;
      this.updateAutoplay();

      const card = e.target.closest('[data-media-card]');
      if (!card || card.dataset.tapped !== undefined) return;
      this.clearTappedState();
    }, { passive: true, signal });

    const onTouchRelease = () => {
      this.isPointerPaused = false;
      this.updateAutoplay();
    };
    this.track.addEventListener('touchend', onTouchRelease, { passive: true, signal });
    this.track.addEventListener('touchcancel', onTouchRelease, { passive: true, signal });

    document.addEventListener('touchstart', (e) => {
      if (!e.target.closest('[data-media-card]')) this.clearTappedState();
    }, { passive: true, signal });

    this.track.addEventListener('scroll', () => {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.handleScroll());

      if (this.clonesAdded) {
        if (this.scrollSettleTimer) clearTimeout(this.scrollSettleTimer);
        this.scrollSettleTimer = setTimeout(() => this.maybeResetCloneRegion(), 150);
      }
    }, { passive: true, signal });

    window.addEventListener('resize', () => {
      // Ignore iOS address-bar resizes (height-only)
      if (window.innerWidth === this.windowWidth) return;
      this.windowWidth = window.innerWidth;

      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        // Cancel in-flight scroll/snap so applyInstantPosition isn't overwritten
        if (this.scrollAnimationId) {
          cancelAnimationFrame(this.scrollAnimationId);
          this.scrollAnimationId = null;
          this.track.style.scrollSnapType = '';
        }
        if (this.snapTimer) {
          clearTimeout(this.snapTimer);
          this.snapTimer = null;
        }
        this.pendingSnap = null;

        this.updateVisibility();
        this.updateControlsVisibility();
        this.syncClones();
        this.measureDimensions();
        this.applyInstantPosition();
        this.updateAutoplay();
      });
    }, { passive: true, signal });

    document.addEventListener('visibilitychange', () => this.updateAutoplay(), { signal });
    this.reducedMotionQuery?.addEventListener?.('change', () => this.updateAutoplay(), { signal });
  }

  destroy() {
    this.stopAutoplay();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
    if (this.scrollAnimationId) cancelAnimationFrame(this.scrollAnimationId);
    if (this.snapTimer) clearTimeout(this.snapTimer);
    if (this.scrollSettleTimer) clearTimeout(this.scrollSettleTimer);
    this.abortController.abort();
    this.teardownClones();
    delete this.root.dataset.sliderInit;
  }
}

const lazyIO = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => { entries.forEach((entry) => { if (entry.isIntersecting) { lazyIO.unobserve(entry.target); instances.set(entry.target, new CommunityShowcase(entry.target)); } }); }, { rootMargin: '600px' }) : null;
function initSections(container = document) {
  const selector = '[data-slider][data-slider-type="community-showcase"]:not([data-slider-init])';
  const roots = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);
  roots.forEach((root) => { root.dataset.sliderInit = 'true'; if (lazyIO) lazyIO.observe(root); else instances.set(root, new CommunityShowcase(root)); });
}

function destroySections(container = document) {
  const selector = '[data-slider][data-slider-type="community-showcase"][data-slider-init]';
  const roots = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);
  roots.forEach((root) => {
    lazyIO?.unobserve(root);
    instances.get(root)?.destroy();
    instances.delete(root);
  });
}

initSections();
document.addEventListener('shopify:section:load', (e) => initSections(e.target));
document.addEventListener('shopify:section:unload', (e) => destroySections(e.target));
